import { forwardRef } from 'react'
import classNames from 'classnames'
import Input from '../Input'
import Button from '../Button'
import type { CommonProps, TypeAttributes } from '../@types/common'
import type { InputProps } from '../Input'
import type { ButtonProps } from '../Button'
import type {
    ChangeEvent,
    FocusEvent,
    MouseEvent,
    ReactNode,
    HTMLInputTypeAttribute,
} from 'react'

export interface InputActionButtonProps extends CommonProps {
    /** Id applied to the inner input (pair it with a `<label htmlFor>`). */
    id?: string
    /** Controlled value of the input. */
    value?: string
    /** Uncontrolled initial value of the input. */
    defaultValue?: string
    /** Placeholder text shown inside the input. */
    placeholder?: string
    /** Native input type. Defaults to `'text'`. */
    type?: HTMLInputTypeAttribute
    /** Disables both the input and the action button. */
    disabled?: boolean
    /** Applies invalid (error) styling to the input. */
    invalid?: boolean
    /** Optional adornment rendered at the start of the input. */
    prefix?: ReactNode
    /** Optional adornment rendered at the end of the input. */
    suffix?: ReactNode
    /** Control size shared by the input and the button so their heights line up. */
    size?: TypeAttributes.ControlSize
    /** Native autoComplete attribute forwarded to the input. */
    autoComplete?: string
    /** Fired on every keystroke in the input. */
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void
    /** Fired when the input loses focus. */
    onInputBlur?: (event: FocusEvent<HTMLInputElement>) => void

    /**
     * Icon-only content for a square action button. Defaults to an ellipsis
     * (`⋯`). Ignored when `actionLabel` is set.
     */
    actionIcon?: ReactNode
    /**
     * Text label for the action button. Renders a regular (non-square) button
     * instead of the icon button.
     */
    actionLabel?: ReactNode
    /** Disables only the action button while leaving the input editable. */
    actionDisabled?: boolean
    /** Shows a spinner inside the action button (e.g. while its action runs). */
    actionLoading?: boolean
    /** Visual variant of the action button. Defaults to `'default'`. */
    actionVariant?: ButtonProps['variant']
    /** Color token of the action button, e.g. `'blue-500'`. */
    actionColor?: string
    /** Shape of the action button. */
    actionShape?: ButtonProps['shape']
    /** Accessible label for an icon-only action button. */
    actionAriaLabel?: string
    /** Fired when the action button is clicked. */
    onAction?: (event: MouseEvent<HTMLButtonElement>) => void

    /** Gap, in pixels, between the input and the action button. Defaults to 8. */
    gap?: number
    /** Escape hatch — extra props forwarded to the inner Input. */
    inputProps?: Partial<InputProps>
    /** Escape hatch — extra props forwarded to the inner Button. */
    buttonProps?: Partial<ButtonProps>
}

const getValueProps = (
    value: string | undefined,
    defaultValue: string | undefined,
): { value?: string; defaultValue?: string } => {
    if (value !== undefined) {
        return { value }
    }
    if (defaultValue !== undefined) {
        return { defaultValue }
    }
    return {}
}

/**
 * InputActionButton — a compound control pairing a text Input with a trailing
 * action Button (an `⋯` icon button by default). Both controls derive their
 * height from the same `size`, so the icon button renders as a square that
 * lines up with the input. See the DropdownShowcase input/button pairs for the
 * design reference.
 */
const InputActionButton = forwardRef<HTMLInputElement, InputActionButtonProps>(
    (props, ref) => {
        const {
            id,
            value,
            defaultValue,
            placeholder,
            type = 'text',
            disabled = false,
            invalid = false,
            prefix,
            suffix,
            size,
            autoComplete,
            onChange,
            onInputBlur,
            actionIcon = '⋯',
            actionLabel,
            actionDisabled = false,
            actionLoading = false,
            actionVariant = 'default',
            actionColor,
            actionShape,
            actionAriaLabel,
            onAction,
            gap = 8,
            className,
            style,
            inputProps,
            buttonProps,
        } = props

        // A non-empty actionLabel renders a text button; otherwise the button is
        // an icon-only square (the `actionIcon`).
        const hasLabel =
            actionLabel !== undefined &&
            actionLabel !== null &&
            actionLabel !== ''

        // Pass exactly one of value/defaultValue so Input is not forced into
        // controlled mode when the caller only supplies a default.
        const valueProps = getValueProps(value, defaultValue)

        return (
            <div
                className={classNames(
                    'input-action-button flex items-center',
                    className,
                )}
                style={{ gap: `${gap}px`, ...style }}
            >
                <div className="flex-1 min-w-0">
                    <Input
                        ref={ref}
                        id={id}
                        type={type}
                        size={size}
                        placeholder={placeholder}
                        disabled={disabled}
                        invalid={invalid}
                        prefix={prefix}
                        suffix={suffix}
                        autoComplete={autoComplete}
                        onChange={onChange}
                        onBlur={onInputBlur}
                        {...valueProps}
                        {...inputProps}
                    />
                </div>
                <Button
                    type="button"
                    size={size}
                    variant={actionVariant}
                    color={actionColor}
                    shape={actionShape}
                    disabled={disabled || actionDisabled}
                    loading={actionLoading}
                    icon={hasLabel ? undefined : actionIcon}
                    aria-label={hasLabel ? undefined : actionAriaLabel}
                    className="flex-shrink-0 !h-8 !w-8"
                    onClick={onAction}
                    {...buttonProps}
                >
                    {hasLabel ? actionLabel : undefined}
                </Button>
            </div>
        )
    },
)

InputActionButton.displayName = 'InputActionButton'

export default InputActionButton
